import { useEffect } from 'react';
import { Box, Button, Flex, toaster } from '@optiaxiom/react';
import { IconArrowRotateLeft } from '@optimizely/axiom-icons';

import { EmptyPage } from '@frontend/components';
import FullScreenLoader from '@frontend/components/FullScreenLoader';
import { ToolsTable } from '@frontend/components/tools-table';
import {
  useGetTools,
  useRestoreToolsInPrimaryAgent,
  useToolEnabledInPrimaryAgent,
  useToolStatus,
} from '@frontend/features/tools-management/hooks';
import { useOpalUser } from '@frontend/hooks';
import { Tool } from '@frontend/types';

type ToolsTabProps = {
  filterText: string;
};

export function ToolsTab({ filterText }: ToolsTabProps) {
  const { opalUser } = useOpalUser();

  const { toolsResponse, isPending, error } = useGetTools(opalUser.instance.instance_id, true);
  const { toggleStatus, isUpdating, updatingToolId } = useToolStatus();
  const {
    toggleEnabledInPrimaryAgent,
    isUpdating: isUpdatingEnabledInPrimaryAgent,
    updatingToolId: updatingToolIdEnabledInPrimaryAgent,
  } = useToolEnabledInPrimaryAgent();
  const { restoreToolsInPrimaryAgent, isRestoring } = useRestoreToolsInPrimaryAgent();

  useEffect(() => {
    if (error) {
      toaster.create(error.message || 'Failed to load tools', { type: 'danger' });
    }
  }, [error]);

  const handleToggleActive = (toolId: string, isDisabled: boolean) => {
    toggleStatus({ toolId, isDisabled });
  };

  const handleToggleEnabledInPrimaryAgent = (
    toolName: string,
    isEnabledInPrimaryAgent: boolean
  ) => {
    toggleEnabledInPrimaryAgent({ toolName, isEnabledInPrimaryAgent });
  };

  if (isPending || isRestoring) {
    return <FullScreenLoader />;
  }

  if (!toolsResponse || toolsResponse.functions.length === 0) {
    return <EmptyPage title="No tools found." description="Try adding new tool registry." />;
  }

  const filteredTools = toolsResponse.functions.filter((tool: Tool) => {
    if (!filterText) return true;
    const lowerCaseFilter = filterText.toLowerCase();
    return (
      tool.name.toLowerCase().includes(lowerCaseFilter) ||
      (tool.description && tool.description.toLowerCase().includes(lowerCaseFilter))
    );
  });

  if (filteredTools.length === 0) {
    return <EmptyPage title="No tools match your search." description="Try a different search." />;
  }

  return (
    <>
      {!isPending && !error && !isRestoring && (
        <Flex gap="16">
          <Box display="flex" justifyContent="flex-end">
            <Button
              onClick={() => restoreToolsInPrimaryAgent()}
              disabled={
                isRestoring ||
                isUpdatingEnabledInPrimaryAgent ||
                isUpdating ||
                !toolsResponse.is_allow_list_available
              }
              appearance="subtle"
              icon={<IconArrowRotateLeft />}
            >
              Restore tools for chat
            </Button>
          </Box>
          <ToolsTable
            tools={filteredTools}
            isUpdating={isUpdating}
            updatingToolId={updatingToolId}
            isUpdatingEnabledInPrimaryAgent={isUpdatingEnabledInPrimaryAgent}
            updatingToolIdEnabledInPrimaryAgent={updatingToolIdEnabledInPrimaryAgent}
            onToggleActive={handleToggleActive}
            onToggleEnabledInPrimaryAgent={handleToggleEnabledInPrimaryAgent}
          />
        </Flex>
      )}
    </>
  );
}
